import { Flag, Check, Palette } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { useTemplateDesigner } from '../TemplateDesignerContext';
import type { BannerStyle } from '../types';

const BANNER_STYLES: { id: BannerStyle; name: string; description: string }[] = [
  { id: 'none', name: 'None', description: 'No banner' },
  { id: 'solid', name: 'Solid', description: 'Clean single color' },
  { id: 'gradient', name: 'Gradient', description: 'Smooth diagonal blend' },
  { id: 'flowing-curves', name: 'Flowing Curves', description: 'Elegant S-curve waves' },
  { id: 'organic-blob', name: 'Organic Blob', description: 'Modern fluid shapes' },
  { id: 'angular-sweep', name: 'Angular Sweep', description: 'Bold diagonal with accent' },
  { id: 'layered-waves', name: 'Layered Waves', description: 'Multiple flowing layers' },
  { id: 'mesh-gradient', name: 'Mesh Blend', description: 'Soft gradient mesh' },
  { id: 'ribbon-accent', name: 'Ribbon', description: 'Diagonal ribbon stripe' },
  { id: 'circuit-lines', name: 'Circuit', description: 'Tech-inspired lines' },
  { id: 'topographic', name: 'Topographic', description: 'Contour line pattern' },
  { id: 'aurora', name: 'Aurora', description: 'Northern lights flow' },
];

// Helper to lighten/darken a hex color
function adjustColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, Math.max(0, (num >> 16) + amt));
  const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amt));
  const B = Math.min(255, Math.max(0, (num & 0x0000ff) + amt));
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
}

// Helper to add transparency to hex color
function hexToRgba(hex: string, alpha: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function BannerPanel() {
  const { settings, updateNestedSettings, setActiveSection } = useTemplateDesigner();
  const { banner, colorTheme } = settings;

  // Banner colors are derived from the color theme
  const primaryColor = colorTheme.primary;
  const secondaryColor = colorTheme.secondary;

  const renderBannerPreview = (style: BannerStyle) => {
    const color = primaryColor;
    const secondary = secondaryColor;
    const lighter = adjustColor(color, 30);
    const darker = adjustColor(color, -20);

    switch (style) {
      case 'none':
        return (
          <div className="w-full h-full bg-muted/50 flex items-center justify-center text-xs text-muted-foreground">
            No Banner
          </div>
        );

      case 'solid':
        return <div className="w-full h-full" style={{ backgroundColor: color }} />;

      case 'gradient':
        return (
          <div
            className="w-full h-full"
            style={{
              background: `linear-gradient(135deg, ${color} 0%, ${secondary} 100%)`,
            }}
          />
        );

      case 'flowing-curves':
        return (
          <div className="w-full h-full relative overflow-hidden" style={{ backgroundColor: color }}>
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 100" preserveAspectRatio="none">
              <path
                d="M-50 80 C100 20, 200 100, 350 40 C400 20, 420 60, 450 50 L450 120 L-50 120 Z"
                fill={hexToRgba(secondary, 0.6)}
              />
              <path
                d="M-50 90 C80 50, 180 110, 320 60 C380 40, 420 80, 450 70 L450 120 L-50 120 Z"
                fill={hexToRgba(lighter, 0.4)}
              />
              <path
                d="M-20 30 Q100 10, 200 25 T400 15"
                fill="none"
                stroke={hexToRgba('#ffffff', 0.2)}
                strokeWidth="2"
              />
            </svg>
          </div>
        );

      case 'organic-blob':
        return (
          <div className="w-full h-full relative overflow-hidden" style={{ backgroundColor: color }}>
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 100" preserveAspectRatio="none">
              <path
                d="M-80 -20 C-40 30, 40 80, 120 60 C180 45, 160 -10, 100 -30 C40 -50, -40 -60, -80 -20 Z"
                fill={hexToRgba(darker, 0.5)}
              />
              <path
                d="M100 120 C160 90, 220 60, 250 80 C280 100, 250 130, 180 140 C110 150, 60 140, 100 120 Z"
                fill={hexToRgba('#ffffff', 0.25)}
              />
              <path
                d="M280 -20 C340 20, 380 60, 420 40 L420 120 L320 120 C300 100, 260 40, 280 -20 Z"
                fill={hexToRgba(secondary, 0.7)}
              />
              <rect x="200" y="-10" width="8" height="130" fill={hexToRgba('#ffffff', 0.1)} transform="rotate(15 200 50)" />
              <rect x="220" y="-10" width="4" height="130" fill={hexToRgba('#ffffff', 0.15)} transform="rotate(15 220 50)" />
            </svg>
          </div>
        );

      case 'angular-sweep':
        return (
          <div className="w-full h-full relative overflow-hidden" style={{ backgroundColor: color }}>
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 100" preserveAspectRatio="none">
              <path d="M0 0 L280 0 L180 100 L0 100 Z" fill={darker} />
              <path
                d="M280 0 C300 30, 220 70, 180 100 L200 100 C240 70, 320 30, 300 0 Z"
                fill={hexToRgba('#ffffff', 0.2)}
              />
              <path d="M300 0 L320 0 L220 100 L200 100 Z" fill={secondary} />
              <path d="M320 0 L328 0 L228 100 L220 100 Z" fill={hexToRgba('#ffffff', 0.4)} />
            </svg>
          </div>
        );

      case 'layered-waves':
        return (
          <div className="w-full h-full relative overflow-hidden" style={{ backgroundColor: color }}>
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 100" preserveAspectRatio="none">
              <path
                d="M0 50 Q50 30, 100 50 T200 50 T300 50 T400 50 L400 100 L0 100 Z"
                fill={hexToRgba(darker, 0.6)}
              />
              <path
                d="M0 60 Q60 40, 120 60 T240 55 T360 65 T480 55 L480 100 L0 100 Z"
                fill={hexToRgba(secondary, 0.5)}
              />
              <path
                d="M0 75 Q80 55, 160 75 T320 70 T480 80 L480 100 L0 100 Z"
                fill={hexToRgba(lighter, 0.4)}
              />
              <path d="M0 20 Q100 10, 200 25 T400 15" fill="none" stroke={hexToRgba('#ffffff', 0.15)} strokeWidth="1" />
              <path d="M0 30 Q120 20, 240 35 T480 25" fill="none" stroke={hexToRgba('#ffffff', 0.1)} strokeWidth="1" />
            </svg>
          </div>
        );

      case 'mesh-gradient':
        return (
          <div
            className="w-full h-full relative overflow-hidden"
            style={{
              background: `
                radial-gradient(ellipse 80% 100% at 10% 20%, ${color} 0%, transparent 50%),
                radial-gradient(ellipse 60% 80% at 90% 80%, ${secondary} 0%, transparent 50%),
                radial-gradient(ellipse 70% 90% at 50% 50%, ${lighter} 0%, transparent 60%),
                linear-gradient(135deg, ${color} 0%, ${secondary} 100%)
              `,
            }}
          >
            <div
              className="absolute inset-0 opacity-[0.03]"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
              }}
            />
          </div>
        );

      case 'ribbon-accent':
        return (
          <div className="w-full h-full relative overflow-hidden" style={{ backgroundColor: color }}>
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 100" preserveAspectRatio="none">
              <path d="M-20 120 L140 -20 L180 -20 L20 120 Z" fill={secondary} />
              <path d="M20 120 L180 -20 L195 -20 L35 120 Z" fill={hexToRgba('#000000', 0.15)} />
              <path d="M35 120 L195 -20 L210 -20 L50 120 Z" fill={hexToRgba('#ffffff', 0.3)} />
              <path d="M350 100 Q380 60, 400 80 L400 100 Z" fill={hexToRgba(secondary, 0.3)} />
              <path d="M370 100 Q390 70, 400 85 L400 100 Z" fill={hexToRgba('#ffffff', 0.2)} />
            </svg>
          </div>
        );

      case 'circuit-lines':
        return (
          <div className="w-full h-full relative overflow-hidden" style={{ backgroundColor: color }}>
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 100" preserveAspectRatio="none">
              <line x1="0" y1="20" x2="100" y2="20" stroke={hexToRgba('#ffffff', 0.15)} strokeWidth="1" />
              <line x1="120" y1="20" x2="250" y2="20" stroke={hexToRgba('#ffffff', 0.15)} strokeWidth="1" />
              <line x1="50" y1="40" x2="180" y2="40" stroke={hexToRgba('#ffffff', 0.1)} strokeWidth="1" />
              <line x1="200" y1="40" x2="350" y2="40" stroke={hexToRgba('#ffffff', 0.1)} strokeWidth="1" />
              <line x1="0" y1="60" x2="80" y2="60" stroke={hexToRgba('#ffffff', 0.12)} strokeWidth="1" />
              <line x1="100" y1="60" x2="300" y2="60" stroke={hexToRgba('#ffffff', 0.12)} strokeWidth="1" />
              <line x1="150" y1="80" x2="400" y2="80" stroke={hexToRgba('#ffffff', 0.08)} strokeWidth="1" />
              <line x1="100" y1="20" x2="100" y2="60" stroke={hexToRgba('#ffffff', 0.15)} strokeWidth="1" />
              <line x1="180" y1="40" x2="180" y2="80" stroke={hexToRgba('#ffffff', 0.1)} strokeWidth="1" />
              <line x1="250" y1="20" x2="250" y2="40" stroke={hexToRgba('#ffffff', 0.12)} strokeWidth="1" />
              <line x1="300" y1="40" x2="300" y2="80" stroke={hexToRgba('#ffffff', 0.1)} strokeWidth="1" />
              <circle cx="100" cy="20" r="3" fill={secondary} />
              <circle cx="100" cy="60" r="2" fill={hexToRgba('#ffffff', 0.3)} />
              <circle cx="180" cy="40" r="3" fill={secondary} />
              <circle cx="250" cy="20" r="2" fill={hexToRgba('#ffffff', 0.3)} />
              <circle cx="300" cy="60" r="3" fill={secondary} />
              <rect x="0" y="0" width="400" height="100" fill="url(#circuitGrad)" />
              <defs>
                <linearGradient id="circuitGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={hexToRgba(secondary, 0.2)} />
                  <stop offset="100%" stopColor="transparent" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        );

      case 'topographic':
        return (
          <div className="w-full h-full relative overflow-hidden" style={{ backgroundColor: color }}>
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 100" preserveAspectRatio="none">
              <path d="M-20 90 Q80 70, 150 85 T280 75 T400 90" fill="none" stroke={hexToRgba('#ffffff', 0.1)} strokeWidth="1.5" />
              <path d="M-20 75 Q60 55, 130 70 T260 60 T400 75" fill="none" stroke={hexToRgba('#ffffff', 0.12)} strokeWidth="1.5" />
              <path d="M-20 60 Q40 40, 110 55 T240 45 T400 60" fill="none" stroke={hexToRgba('#ffffff', 0.14)} strokeWidth="1.5" />
              <path d="M-20 45 Q20 25, 90 40 T220 30 T400 45" fill="none" stroke={hexToRgba('#ffffff', 0.16)} strokeWidth="1.5" />
              <path d="M-20 30 Q0 10, 70 25 T200 15 T400 30" fill="none" stroke={hexToRgba('#ffffff', 0.18)} strokeWidth="1.5" />
              <path d="M50 15 Q100 5, 150 12 T250 8" fill="none" stroke={hexToRgba('#ffffff', 0.15)} strokeWidth="1" />
              <path d="M300 100 Q340 60, 380 70 T420 50 L420 100 Z" fill={hexToRgba(secondary, 0.3)} />
            </svg>
          </div>
        );

      case 'aurora':
        return (
          <div
            className="w-full h-full relative overflow-hidden"
            style={{
              background: `linear-gradient(180deg, ${darker} 0%, ${color} 50%, ${lighter} 100%)`,
            }}
          >
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 100" preserveAspectRatio="none">
              <path
                d="M0 30 Q50 10, 100 30 T200 20 T300 35 T400 25 L400 60 Q350 40, 300 55 T200 50 T100 60 T0 50 Z"
                fill={hexToRgba(secondary, 0.4)}
              />
              <path
                d="M0 40 Q60 20, 120 40 T240 30 T360 45 T480 35 L480 70 Q420 50, 360 65 T240 60 T120 70 T0 60 Z"
                fill={hexToRgba(lighter, 0.3)}
              />
              <path
                d="M-40 50 Q40 30, 100 50 T200 40 T300 55 T420 45 L420 80 Q360 60, 300 75 T200 70 T100 80 T-40 70 Z"
                fill={hexToRgba('#ffffff', 0.15)}
              />
              <rect x="80" y="0" width="2" height="100" fill={hexToRgba('#ffffff', 0.05)} />
              <rect x="150" y="0" width="3" height="100" fill={hexToRgba(secondary, 0.1)} />
              <rect x="220" y="0" width="2" height="100" fill={hexToRgba('#ffffff', 0.05)} />
              <rect x="300" y="0" width="4" height="100" fill={hexToRgba(secondary, 0.08)} />
              <rect x="360" y="0" width="2" height="100" fill={hexToRgba('#ffffff', 0.05)} />
            </svg>
          </div>
        );

      default:
        return <div className="w-full h-full" style={{ backgroundColor: color }} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Enable Toggle */}
      <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50 border border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-primary/50 flex items-center justify-center">
            <Flag className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <Label className="text-sm font-semibold">Document Banner</Label>
            <p className="text-xs text-muted-foreground">Add a branded header banner</p>
          </div>
        </div>
        <Switch
          checked={banner.enabled}
          onCheckedChange={(checked) => updateNestedSettings('banner', { enabled: checked })}
        />
      </div>

      {banner.enabled && (
        <div className="space-y-6">
          {/* Banner Style Selection - Grid for more visual options */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">Banner Style</Label>
            <div className="grid grid-cols-2 gap-2">
              {BANNER_STYLES.map((style) => {
                const isSelected = banner.style === style.id;
                return (
                  <button
                    key={style.id}
                    onClick={() => updateNestedSettings('banner', { style: style.id })}
                    className={`
                      relative flex flex-col rounded-xl border-2 transition-all text-left overflow-hidden
                      ${isSelected
                        ? 'border-primary ring-2 ring-primary/20'
                        : 'border-border hover:border-primary/30 hover:bg-accent/30'
                      }
                    `}
                  >
                    {/* Mini Preview */}
                    <div className="w-full h-14 overflow-hidden">
                      {renderBannerPreview(style.id)}
                    </div>

                    {/* Info */}
                    <div className="p-2 bg-background">
                      <p className={`text-xs font-medium truncate ${isSelected ? 'text-primary' : ''}`}>
                        {style.name}
                      </p>
                      <p className="text-[10px] text-muted-foreground truncate">
                        {style.description}
                      </p>
                    </div>

                    {/* Checkmark */}
                    {isSelected && (
                      <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-sm">
                        <Check className="w-3 h-3" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Color Info - Links to Colors tab */}
          {banner.style !== 'none' && (
            <>
              <button
                onClick={() => setActiveSection('color')}
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-border bg-muted/30 hover:bg-accent/50 transition-colors text-left"
              >
                <div className="flex gap-1 flex-shrink-0">
                  <div
                    className="w-6 h-6 rounded-md border border-border shadow-sm"
                    style={{ backgroundColor: primaryColor }}
                  />
                  <div
                    className="w-6 h-6 rounded-md border border-border shadow-sm"
                    style={{ backgroundColor: secondaryColor }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium">Using theme colors</p>
                  <p className="text-[10px] text-muted-foreground">Edit in Colors tab</p>
                </div>
                <Palette className="w-4 h-4 text-muted-foreground" />
              </button>

              {/* Banner Height */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Banner Height</Label>
                  <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-1 rounded">
                    {banner.height}px
                  </span>
                </div>
                <Slider
                  value={[banner.height]}
                  onValueChange={([value]) => updateNestedSettings('banner', { height: value })}
                  min={40}
                  max={180}
                  step={10}
                  className="py-2"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Subtle</span>
                  <span>Bold</span>
                </div>
              </div>

              {/* Full Width Preview */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Live Preview</Label>
                <div className="rounded-xl overflow-hidden border border-border shadow-sm bg-white">
                  <div style={{ height: `${Math.min(banner.height, 120)}px` }}>
                    {renderBannerPreview(banner.style)}
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="flex justify-between items-start">
                      <div className="space-y-1.5">
                        <div className="h-4 w-28 bg-muted rounded" />
                        <div className="h-2 w-20 bg-muted/60 rounded" />
                      </div>
                      <div className="text-right space-y-1.5">
                        <div className="h-5 w-20 bg-primary/20 rounded" />
                        <div className="h-2 w-14 bg-muted/60 rounded ml-auto" />
                      </div>
                    </div>
                    <div className="h-px bg-border" />
                    <div className="space-y-1.5">
                      <div className="h-2 w-full bg-muted/40 rounded" />
                      <div className="h-2 w-3/4 bg-muted/40 rounded" />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
